var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { plainToInstance } from "class-transformer";
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { DocumentRetrievalAuthType, DocumentRetrievalFailureReason, DocumentRetrievalLocationStatus, DocumentRetrievalTaskSource, DocumentVectorizationStatus, EpicIntegration, } from "healthex-common/document.retrieval.dtos";
import { indexBy } from "healthex-common/utils";
import { computed, observable, runInAction, when } from "mobx";
import { FastenCallbackEventType } from "../Infra/fasten.callback.dtos";
import { getFastenApiMode, getFastenPublicId } from "../Infra/FastenSearchService";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { SmartAuthErrorReason, SmartAuthProvider, SmartAuthState } from "../Logic/SmartAuthManager";
import { DocumentRetrievalUIState } from "./DocumentRetrievalUIState";
import { FacilitatedFHIRAuthErrorReason, FacilitatedFHIRAuthState } from "./FacilitatedFHIRAuthManager";
import { PDDA_ALL_ROUTES } from "./PatientConsentRoute";
import { PatientConsentSessionState } from "./PatientConsentSession";
import { PatientConsentViewModel } from "./PatientConsentViewModel";
const t = getObservableT("patients");
export var DocumentRetrievalStep;
(function (DocumentRetrievalStep) {
    DocumentRetrievalStep["LOADING"] = "LOADING";
    DocumentRetrievalStep["RECORDS_NOT_FOUND"] = "RECORDS_NOT_FOUND";
    DocumentRetrievalStep["RECORDS_FOUND_LIST"] = "RECORDS_FOUND_LIST";
    DocumentRetrievalStep["DOCUMENT_SELECTION"] = "DOCUMENT_SELECTION";
    DocumentRetrievalStep["ADD_AND_SHARE"] = "ADD_AND_SHARE";
    DocumentRetrievalStep["CONTINUE_WITHOUT_SHARING"] = "CONTINUE_WITHOUT_SHARING";
    DocumentRetrievalStep["TECHNICAL_ERROR"] = "TECHNICAL_ERROR";
    DocumentRetrievalStep["REVIEW_AND_SHARE"] = "REVIEW_LOCATIONS";
    DocumentRetrievalStep["LOCATION_LOGIN"] = "LOCATION_LOGIN";
    DocumentRetrievalStep["HUR_LOADING"] = "HUR_LOADING";
    DocumentRetrievalStep["RESTORING_SESSION"] = "RESTORING_SESSION";
})(DocumentRetrievalStep || (DocumentRetrievalStep = {}));
export class DocumentRetrievalViewModel extends PatientConsentViewModel {
    get uiState() {
        return this._uiState;
    }
    updateConsentLocations() {
        const consentLocations = this.locationList.map((loc) => ({
            name: loc.name,
            logoUrl: loc.logoUrl,
            status: loc.status,
            isExcluded: loc.isExcluded,
            documents: loc.documents.map((doc) => ({ status: doc.status })),
        }));
        this.patientConsentUIManager.updateDocumentRetrievalLocations(consentLocations);
    }
    get retrievalError() {
        return this.documentRetrievalManager.retrievalErrors.length > 0;
    }
    get isAIFlow() {
        var _a, _b;
        return (_b = (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.isAIFlow) !== null && _b !== void 0 ? _b : false;
    }
    get isVectorizationInProgress() {
        var _a, _b, _c;
        return (this.isAIFlow &&
            (((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.PENDING ||
                ((_b = this.retrieval) === null || _b === void 0 ? void 0 : _b.documentVectorizationStatus) === DocumentVectorizationStatus.IN_PROGRESS ||
                ((_c = this.retrieval) === null || _c === void 0 ? void 0 : _c.documentVectorizationStatus) === DocumentVectorizationStatus.NOT_STARTED));
    }
    get fastenPublicId() {
        return getFastenPublicId(this.authManager.isSandbox, this.environment);
    }
    get isVectorizationComplete() {
        var _a;
        return this.isAIFlow && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.COMPLETE;
    }
    get canAddLocations() {
        if (!!this.retrieval && this.retrieval.allowOnlyTargetedFacilities === true) {
            return false;
        }
        return true;
    }
    get zeroLocationsFound() {
        if (!!this.retrieval && this.retrieval.locations.length === 0) {
            return true;
        }
        return false;
    }
    get explanationText() {
        if (this.isDataUpdate) {
            if (this.zeroLocationsFound) {
                return t("To help you and {{ orgName }} access an updated copy of your health records, HealthEx connects to providers you have received care from.", { orgName: this.organizationName });
            }
            return t("To access an updated copy of your records, HealthEx has connected with the providers below. Some may ask you to log in, depending on their security settings.");
        }
        if (this.zeroLocationsFound) {
            return t("To help you and {{ orgName }} access your health records, HealthEx connects to providers you have received care from.", { orgName: this.organizationName });
        }
        return t("To help you and {{ orgName }} access your records, HealthEx has connected with the providers below. Some may ask you to log in, depending on their security settings.", { orgName: this.organizationName });
    }
    get needsToReauthWithCLEAR() {
        return false;
    }
    get getAuthErrorText() {
        const isClearValidationFailed = this.documentRetrievalManager.retrievalErrors.some((error) => error === DocumentRetrievalFailureReason.CLEAR_VALIDATION_FAILED);
        return isClearValidationFailed ? t("Your identity verification has expired.") : t("Your CLEAR login has expired");
    }
    get isAuthLoading() {
        return (this.smartAuthManager.state.state === SmartAuthState.IN_PROGRESS ||
            this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.IN_PROGRESS);
    }
    get locationList() {
        if (this._uiState.staticRetrievalData) {
            return this._uiState.staticRetrievalData;
        }
        if (!this.retrieval)
            return [];
        const locations = this.retrieval.locations;
        const documents = this.retrieval.documents;
        return locations.map((location) => {
            var _a;
            const locationDocuments = documents.filter((document) => document.locationId === location.id);
            let locationStatus = location.status === DocumentRetrievalLocationStatus.ERROR ? DocumentRetrievalLocationStatus.COMPLETE : location.status;
            let locationExcluded = location.isExcluded;
            if (this._uiState.excludedLoginKeys.includes(location.id)) {
                locationExcluded = true;
            }
            else if (this._uiState.includedLoginKeys.includes(location.id)) {
                locationExcluded = null;
            }
            if (this._uiState.completedLoginKeys.includes(location.id)) {
                locationStatus = DocumentRetrievalLocationStatus.COMPLETE;
            }
            else if (this._uiState.skippedLoginKeys.includes(location.id)) {
                locationStatus = DocumentRetrievalLocationStatus.SKIPPED;
            }
            return {
                key: location.id,
                name: (_a = location.organizationName) !== null && _a !== void 0 ? _a : "",
                logoUrl: location.organizationLogoUrl,
                documents: locationDocuments.map((document) => {
                    var _a;
                    return ({
                        id: document.id,
                        filename: (_a = document.filename) !== null && _a !== void 0 ? _a : "",
                        source: document.source,
                        status: document.status,
                    });
                }),
                status: locationStatus,
                isExcluded: locationExcluded,
                authType: location.authType,
                isPrevious: location.isPrevious,
                metadata: location.organizationMetadata,
                updatedAt: location.updatedAt,
                source: location.source,
            };
        });
    }
    get locationStatusCounts() {
        if (!this.retrieval)
            return {
                total: 0,
                completed: 0,
                error: 0,
                skipped: 0,
                excluded: 0,
            };
        const locations = this.retrieval.locations;
        return {
            total: locations.length,
            completed: locations.filter((l) => l.status === DocumentRetrievalLocationStatus.COMPLETE).length,
            error: locations.filter((l) => l.status === DocumentRetrievalLocationStatus.ERROR).length,
            skipped: locations.filter((l) => l.status === DocumentRetrievalLocationStatus.SKIPPED).length,
            excluded: locations.filter((l) => l.isExcluded).length,
        };
    }
    get isInHurLoading() {
        if (!this.retrieval)
            return false;
        if (!this.retrieval.documentSelectionComplete)
            return false;
        return !this.retrieval.finalized && this.isIAS;
    }
    constructor(container) {
        super(container);
        this.previousTrackedStep = null;
        this.loadingStartTime = null;
        this.didReturnFromAuthFlow = false;
        this.lastObservedPage = null;
        this._uiState = new DocumentRetrievalUIState();
        this.fastenAuthError = null;
        this.fastenCustomSearchModalOpen = false;
        this.documentRetrievalManager = container.documentRetrievalManager;
        this.facilitatedFHIRAuthManager = container.facilitatedFHIRAuthManager;
        this.smartAuthManager = container.smartAuthManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.featureManager = container.featureManager;
        this.epicDirectoryService = container.epicDirectoryService;
    }
    run() {
        if (this._uiState.isStatic) {
            return;
        }
        this.disposer.reaction(() => this.patientConsentUIManager.session.patientConsentSessionState, async (patientConsentSessionState) => {
            var _a;
            if (patientConsentSessionState === PatientConsentSessionState.RESTORING_COMPLETED) {
                const smartAuthData = this.smartAuthManager.getAndClearSmartAuthData();
                const hasFastenAuthCompleted = this.smartAuthManager.hasCompletedAuth && (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN;
                if (this.facilitatedFHIRAuthManager.hasCompletedAuth || hasFastenAuthCompleted) {
                    const isEpicAuthError = this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.ERROR;
                    const isFastenAuthError = hasFastenAuthCompleted && this.smartAuthManager.state.state === SmartAuthState.ERROR;
                    if (isEpicAuthError || isFastenAuthError) {
                        this.didReturnFromAuthFlow = true;
                    }
                    this.restoreDocumentRetrievalUIState();
                }
                if (hasFastenAuthCompleted) {
                    if (this.smartAuthManager.state.state === SmartAuthState.COMPLETED &&
                        this._uiState.currentLocationKeyForLogin &&
                        (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.locationKey) === this._uiState.currentLocationKeyForLogin) {
                        this._uiState.completedLoginKeys.push(this._uiState.currentLocationKeyForLogin);
                    }
                    await this.handleFastenCallbackForCustomUI(smartAuthData);
                    this.patientConsentUIManager.resetRetrievalUIStateAndCompleteRestoration();
                    this.smartAuthManager.resetAuth();
                    window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
                    return;
                }
                if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.SUCCESS) {
                    if (this._uiState.currentLocationKeyForLogin) {
                        this._uiState.completedLoginKeys.push(this._uiState.currentLocationKeyForLogin);
                    }
                    else {
                        const { key, code, fhirEndpoint } = this.facilitatedFHIRAuthManager.state;
                        if (fhirEndpoint) {
                            await this.handleEpicLocationLoginSuccess(key, code, fhirEndpoint);
                        }
                        else {
                            console.error("Epic location missing FHIR endpoint");
                        }
                    }
                    const locations = (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.locations;
                    if (locations) {
                        const locationIndex = indexBy(locations, (location) => location.id);
                        const alreadyCompletedLoginKeys = new Set(this._uiState.completedLoginKeys);
                        this._uiState.loginRequiredLocationKeys = this._uiState.loginRequiredLocationKeys.filter((loginRequiredLocationKey) => {
                            var _a;
                            const status = (_a = locationIndex[loginRequiredLocationKey]) === null || _a === void 0 ? void 0 : _a.status;
                            if (alreadyCompletedLoginKeys.has(loginRequiredLocationKey))
                                return true;
                            return (status === DocumentRetrievalLocationStatus.NEEDS_AUTH ||
                                status === DocumentRetrievalLocationStatus.SKIPPED);
                        });
                    }
                    this.facilitatedFHIRAuthManager.resetAuth();
                }
                this.patientConsentUIManager.resetRetrievalUIStateAndCompleteRestoration();
            }
        }, { fireImmediately: true, name: "FacilitatedFHIRAuthSuccess" });
        this.disposer.reaction(() => this.isLoading, (isLoading) => {
            if (isLoading) {
                this.startLoadingTimer();
            }
            else {
                this.clearLoadingTimer();
            }
        }, { name: "trackLoading", fireImmediately: true });
        this.disposer.reaction(() => this.currentStep, (step) => {
            this.trackScreenView(step);
        }, { name: "trackScreenView", fireImmediately: true });
        this.disposer.reaction(() => {
            const step = this.currentStep;
            return step === DocumentRetrievalStep.LOCATION_LOGIN ? `${step}::${this._uiState.currentLoginIndex}` : step;
        }, () => {
            if (this._uiState.isStatic || this.isScreenshotMode)
                return;
            this.syncStepToRoute(this.currentStep);
        }, { fireImmediately: true, name: "syncDocumentRetrievalStepToRoute" });
        this.disposer.reaction(() => { var _a, _b; return `${(_a = this.patientConsentUIManager.currentPage) !== null && _a !== void 0 ? _a : ""}::${(_b = this.patientConsentUIManager.currentRouteLocationIndex) !== null && _b !== void 0 ? _b : ""}`; }, () => {
            if (this._uiState.isStatic || this.isScreenshotMode)
                return;
            this.handleRouteChangeFromURL(this.patientConsentUIManager.currentPage);
        }, { name: "handleDocumentRetrievalRouteChangeFromURL" });
        this.lastObservedPage = this.patientConsentUIManager.currentPage;
        this.disposer.reaction(() => this.patientConsentUIManager.currentPage, (currentPage) => this.handleBackoutOfHURLoading(currentPage), { name: "backoutOfHURLoading" });
        when(() => this.retrieval !== null, () => {
            if (this.hasCompletedLoginCycle && !this.useTefca) {
                this._uiState.showFastenImmediately = true;
            }
        });
    }
    stop() {
        this.disposer.dispose();
    }
    static parsedLocationLoginIndexInt(raw) {
        if (raw === null || raw === "")
            return null;
        if (!/^-?\d+$/.test(raw))
            return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    }
    static clampLocationLoginIndex(raw, keyCount) {
        const parsed = DocumentRetrievalViewModel.parsedLocationLoginIndexInt(raw);
        const index = parsed !== null && parsed >= 0 ? parsed : 0;
        if (keyCount <= 0)
            return 0;
        return Math.min(index, keyCount - 1);
    }
    handleBackoutOfHURLoading(currentPage) {
        const leftFrom = this.lastObservedPage;
        this.lastObservedPage = currentPage;
        if (this._uiState.isStatic || this.isScreenshotMode)
            return;
        if (!this.isIAS)
            return;
        if (leftFrom !== "ENROLLMENT_PDDA_HUR_LOADING")
            return;
        if (currentPage === null)
            return;
        if (currentPage === "ENROLLMENT_PDDA_HUR_LOADING")
            return;
        if (!DocumentRetrievalViewModel.pddaRoutes.has(currentPage))
            return;
        if (this.locationsCompleted.length === 0) {
            this.patientConsentUIManager.goToPage("ENROLLMENT_PDDA_RECORDS_NOT_FOUND", { replace: true });
        }
        else {
            this.patientConsentUIManager.goToPage("ENROLLMENT_PDDA_DOCUMENT_SELECTION", { replace: true });
        }
        window.location.reload();
    }
    syncStepToRoute(step) {
        var _a;
        const currentPage = this.patientConsentUIManager.currentPage;
        const didReturnFromAuth = this.didReturnFromAuthFlow;
        this.didReturnFromAuthFlow = false;
        if (didReturnFromAuth) {
            this.handleRouteChangeFromURL(currentPage);
            return;
        }
        if (((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentSelectionComplete) && step !== DocumentRetrievalStep.HUR_LOADING)
            return;
        if (currentPage !== null && !DocumentRetrievalViewModel.pddaRoutes.has(currentPage))
            return;
        const stepToRoute = {
            [DocumentRetrievalStep.RECORDS_NOT_FOUND]: "ENROLLMENT_PDDA_RECORDS_NOT_FOUND",
            [DocumentRetrievalStep.RECORDS_FOUND_LIST]: "ENROLLMENT_PDDA_RECORDS_FOUND_LIST",
            [DocumentRetrievalStep.LOCATION_LOGIN]: "ENROLLMENT_PDDA_LOCATION_LOGIN",
            [DocumentRetrievalStep.DOCUMENT_SELECTION]: "ENROLLMENT_PDDA_DOCUMENT_SELECTION",
            [DocumentRetrievalStep.REVIEW_AND_SHARE]: "ENROLLMENT_PDDA_REVIEW_AND_SHARE",
            [DocumentRetrievalStep.CONTINUE_WITHOUT_SHARING]: "ENROLLMENT_PDDA_CONTINUE_WITHOUT_SHARING",
            [DocumentRetrievalStep.HUR_LOADING]: "ENROLLMENT_PDDA_HUR_LOADING",
        };
        const targetRoute = stepToRoute[step];
        if (!targetRoute)
            return;
        if (targetRoute === "ENROLLMENT_PDDA_LOCATION_LOGIN") {
            const targetIndex = this._uiState.currentLoginIndex;
            const currentRaw = this.patientConsentUIManager.currentRouteLocationIndex;
            const currentParsed = DocumentRetrievalViewModel.parsedLocationLoginIndexInt(currentRaw);
            if (currentPage === "ENROLLMENT_PDDA_LOCATION_LOGIN" && currentParsed !== null && currentParsed === targetIndex) {
                return;
            }
            this.patientConsentUIManager.goToPage("ENROLLMENT_PDDA_LOCATION_LOGIN", { locationIndex: String(targetIndex) });
            return;
        }
        if (currentPage === targetRoute)
            return;
        if (currentPage === "ENROLLMENT_PDDA_RECORDS_LOADING") {
            this.patientConsentUIManager.goToPage(targetRoute, { replace: true });
            return;
        }
        this.patientConsentUIManager.goToPage(targetRoute);
    }
    handleRouteChangeFromURL(page) {
        switch (page) {
            case "ENROLLMENT_PDDA_RECORDS_NOT_FOUND": {
                runInAction(() => {
                    this._uiState.hasSeenRecordsNotFoundScreen = false;
                    this._uiState.hasDeclinedSharing = false;
                });
                break;
            }
            case "ENROLLMENT_PDDA_DOCUMENT_SELECTION": {
                if (this.isInHurLoading)
                    break;
                runInAction(() => {
                    this._uiState.forceShowRecordsFoundListScreen = false;
                    if (this._uiState.isInLoginCycle || this._uiState.currentLocationKeyForLogin !== null) {
                        this._uiState.isInLoginCycle = false;
                        this._uiState.currentLocationKeyForLogin = null;
                    }
                    if (!this._uiState.showDocumentSelectionScreen) {
                        this._uiState.showDocumentSelectionScreen = true;
                        this._uiState.hasSeenDocumentSelectionScreen = false;
                        this._uiState.isTransitioningToHurLoading = false;
                    }
                });
                break;
            }
            case "ENROLLMENT_PDDA_RECORDS_FOUND_LIST": {
                runInAction(() => {
                    this._uiState.forceShowRecordsFoundListScreen = true;
                    this._uiState.hasDeclinedSharing = false;
                    const shouldClearActiveLoginCycle = this._uiState.isInLoginCycle ||
                        this._uiState.loginRequiredLocationKeys.length > 0 ||
                        this._uiState.currentLocationKeyForLogin !== null;
                    if (shouldClearActiveLoginCycle) {
                        this._uiState.isInLoginCycle = false;
                        this._uiState.currentLocationKeyForLogin = null;
                        this._uiState.loginRequiredLocationKeys = [];
                        this._uiState.currentLoginIndex = 0;
                    }
                    const keysToReset = this._uiState.originalLoginLocationKeys;
                    if (keysToReset.length > 0) {
                        this._uiState.completedLoginKeys = this._uiState.completedLoginKeys.filter((k) => !keysToReset.includes(k));
                        this._uiState.skippedLoginKeys = this._uiState.skippedLoginKeys.filter((k) => !keysToReset.includes(k));
                    }
                });
                break;
            }
            case "ENROLLMENT_PDDA_LOCATION_LOGIN": {
                const rawLocationIndex = this.patientConsentUIManager.currentRouteLocationIndex;
                const keys = this._uiState.loginRequiredLocationKeys.length > 0
                    ? this._uiState.loginRequiredLocationKeys
                    : this._uiState.originalLoginLocationKeys;
                if (keys.length === 0)
                    break;
                const targetIndex = DocumentRetrievalViewModel.clampLocationLoginIndex(rawLocationIndex, keys.length);
                runInAction(() => {
                    this._uiState.hasDeclinedSharing = false;
                    this._uiState.forceShowRecordsFoundListScreen = false;
                    this._uiState.isInLoginCycle = true;
                    this._uiState.currentLoginIndex = targetIndex;
                    this._uiState.currentLocationKeyForLogin = keys[targetIndex];
                    if (this._uiState.loginRequiredLocationKeys.length === 0) {
                        this._uiState.loginRequiredLocationKeys = [...keys];
                    }
                });
                break;
            }
            case "ENROLLMENT_PDDA_REVIEW_AND_SHARE": {
                runInAction(() => {
                    this._uiState.hasDeclinedSharing = false;
                    this._uiState.forceShowRecordsFoundListScreen = false;
                });
                break;
            }
            case "ENROLLMENT_PDDA_CONTINUE_WITHOUT_SHARING":
                break;
            case "ENROLLMENT_PDDA_HUR_LOADING":
                break;
            default: {
                if (page !== null && !DocumentRetrievalViewModel.pddaRoutes.has(page)) {
                    const { patientId, studyId } = this._uiState;
                    runInAction(() => {
                        this._uiState = new DocumentRetrievalUIState();
                        this._uiState.patientId = patientId;
                        this._uiState.studyId = studyId;
                    });
                }
                break;
            }
        }
    }
    trackScreenView(step) {
        var _a, _b, _c;
        if (step === DocumentRetrievalStep.LOADING) {
            if (this.loadingStartTime === null) {
                this.loadingStartTime = Date.now();
                heap.track("DocumentRetrievalLoadingStart", {
                    patientId: this._uiState.patientId,
                    studyId: this._uiState.studyId,
                    batchId: (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId,
                });
            }
            this.previousTrackedStep = step;
            return;
        }
        if (this.previousTrackedStep === DocumentRetrievalStep.LOADING && this.loadingStartTime !== null) {
            const loadingDuration = Date.now() - this.loadingStartTime;
            const statusCounts = this.locationStatusCounts;
            heap.track("DocumentRetrievalLoadingComplete", {
                patientId: this._uiState.patientId,
                studyId: this._uiState.studyId,
                batchId: (_b = this.retrieval) === null || _b === void 0 ? void 0 : _b.batchId,
                durationSeconds: Math.round(loadingDuration / 1000),
                nextScreen: step,
                totalLocations: statusCounts.total,
                completedLocations: statusCounts.completed,
                errorLocations: statusCounts.error,
                skippedLocations: statusCounts.skipped,
                excludedLocations: statusCounts.excluded,
                needsReauth: this.needsToReauthWithCLEAR,
                zeroLocationsFound: this.zeroLocationsFound,
                timestamp: new Date().toISOString(),
            });
            this.loadingStartTime = null;
        }
        if (step === this.previousTrackedStep) {
            return;
        }
        this.previousTrackedStep = step;
        const eventName = `DocumentRetrieval${this.toPascalCase(step)}`;
        heap.track(eventName, {
            patientId: this._uiState.patientId,
            studyId: this._uiState.studyId,
            batchId: (_c = this.retrieval) === null || _c === void 0 ? void 0 : _c.batchId,
            totalLocations: this.locationList.length,
            zeroLocationsFound: this.zeroLocationsFound,
            hasError: !!this.retrievalError,
        });
    }
    toPascalCase(step) {
        if (!step)
            return "Screen";
        return step
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join("");
    }
    get facilitatedFhirAuthInProgress() {
        if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.IN_PROGRESS) {
            return this.facilitatedFHIRAuthManager.state.key;
        }
        return null;
    }
    get facilitatedFhirAuthErrorKey() {
        if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.ERROR) {
            return this.facilitatedFHIRAuthManager.state.key;
        }
        return null;
    }
    get facilitatedFhirAuthErrorMessage() {
        if (this.facilitatedFHIRAuthManager.state.state !== FacilitatedFHIRAuthState.ERROR)
            return "";
        switch (this.facilitatedFHIRAuthManager.state.error) {
            case FacilitatedFHIRAuthErrorReason.AUTH_WINDOW_CLOSED:
                return t("You closed the tab before we could find your records. Please try logging in again.");
            case FacilitatedFHIRAuthErrorReason.AUTH_FAILED:
                return t("You left the page before we could find your records. Please try logging in again.");
            default:
                return t("Something went wrong while finding your records. Please try logging in again.");
        }
    }
    get fastenAuthInProgress() {
        var _a;
        if (this.smartAuthManager.state.state === SmartAuthState.IN_PROGRESS) {
            return (_a = this.smartAuthManager.state.key) !== null && _a !== void 0 ? _a : null;
        }
        return null;
    }
    get fastenAuthErrorKey() {
        if (this.fastenAuthError) {
            return this.fastenAuthError.locationKey;
        }
        return null;
    }
    get fastenAuthErrorMessage() {
        if (this.fastenAuthError) {
            switch (this.fastenAuthError.error) {
                case SmartAuthErrorReason.LOGIN_WINDOW_CLOSED:
                    return t("You closed the tab before we could find your records. Please try logging in again.");
                case SmartAuthErrorReason.AUTH_FAILED:
                    return t("You left the page before we could find your records. Please try logging in again.");
                default:
                    return t("Something went wrong while finding your records. Please try logging in again.");
            }
        }
        return "";
    }
    get authInProgress() {
        return this.facilitatedFhirAuthInProgress || this.fastenAuthInProgress;
    }
    get authErrorKey() {
        var _a;
        return (_a = this.facilitatedFhirAuthErrorKey) !== null && _a !== void 0 ? _a : this.fastenAuthErrorKey;
    }
    get authErrorMessage() {
        return this.facilitatedFhirAuthErrorMessage || this.fastenAuthErrorMessage;
    }
    get patientInfo() {
        if (this._uiState.patientId === null)
            return null;
        return {
            patientId: this._uiState.patientId,
        };
    }
    get isLoading() {
        if (this._uiState.isStatic)
            return false;
        if (!!this.retrieval && this.retrieval.tefcaQueries.every((query) => query.isComplete)) {
            return false;
        }
        if (this.retrievalError) {
            return false;
        }
        return true;
    }
    get isTEFCAError() {
        return !this.needsToReauthWithCLEAR && this.retrievalError;
    }
    get useTefca() {
        return this.patientConsentUIManager.useTefca;
    }
    get currentStep() {
        if (this._uiState.stepToShow !== undefined) {
            return this._uiState.stepToShow;
        }
        if (this.isScreenshotMode) {
            return this.anyLocationNotAddedViaFasten ? DocumentRetrievalStep.REVIEW_AND_SHARE : DocumentRetrievalStep.RECORDS_NOT_FOUND;
        }
        if (this.patientConsentUIManager.session.patientConsentSessionState !== PatientConsentSessionState.INITIALIZED) {
            return DocumentRetrievalStep.RESTORING_SESSION;
        }
        if (this.isLoading) {
            return DocumentRetrievalStep.LOADING;
        }
        if (this.needsToReauthWithCLEAR || this.isTEFCAError) {
            return DocumentRetrievalStep.TECHNICAL_ERROR;
        }
        if (this.zeroLocationsFound && !this.needsToReauthWithCLEAR && !this._uiState.hasSeenRecordsNotFoundScreen && this.useTefca) {
            return DocumentRetrievalStep.RECORDS_NOT_FOUND;
        }
        if (this._uiState.hasDeclinedSharing) {
            return DocumentRetrievalStep.CONTINUE_WITHOUT_SHARING;
        }
        if (this.isInHurLoading || this._uiState.isTransitioningToHurLoading) {
            return DocumentRetrievalStep.HUR_LOADING;
        }
        if (this._uiState.currentLocationKeyForLogin !== null) {
            return DocumentRetrievalStep.LOCATION_LOGIN;
        }
        if (this._uiState.forceShowRecordsFoundListScreen) {
            return DocumentRetrievalStep.RECORDS_FOUND_LIST;
        }
        if (this.showDocumentSelectionScreen) {
            return DocumentRetrievalStep.DOCUMENT_SELECTION;
        }
        if (!this.hasCompletedLoginCycle) {
            return DocumentRetrievalStep.RECORDS_FOUND_LIST;
        }
        if (this._uiState.hasSeenDocumentSelectionScreen && this.locationsCompleted.length === 0) {
            return DocumentRetrievalStep.CONTINUE_WITHOUT_SHARING;
        }
        return DocumentRetrievalStep.REVIEW_AND_SHARE;
    }
    get showDocumentSelectionScreen() {
        return this._uiState.showDocumentSelectionScreen && this.hasCompletedLoginCycle;
    }
    get isIAS() {
        return this.patientConsentUIManager.isIAS;
    }
    addProvidersFromRecordsNotFound() {
        this._uiState.showFastenImmediately = true;
        this._uiState.showDocumentSelectionScreen = true;
        this._uiState.hasSeenRecordsNotFoundScreen = true;
    }
    async addProvidersFromTechnicalError() {
        var _a;
        if (!((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId))
            return;
        this._uiState.hasSeenRecordsNotFoundScreen = true;
        if (this.hasCompletedLoginCycle) {
            this._uiState.showFastenImmediately = true;
        }
        await this.documentRetrievalManager.skipTEFCARetrieval(this.retrieval.batchId);
    }
    continueFromRecordsNotFound() {
        runInAction(() => {
            this._uiState.hasDeclinedSharing = true;
            this._uiState.hasSeenRecordsNotFoundScreen = true;
        });
    }
    continueWithoutSharingRecords() {
        this._uiState.hasDeclinedSharing = true;
    }
    async continueFromDocumentSelection() {
        await this.updateExcludedLocations();
        if (this.isIAS && this.locationsCompleted.length > 0) {
            runInAction(() => {
                this._uiState.isTransitioningToHurLoading = true;
                this._uiState.hasSeenDocumentSelectionScreen = true;
                this._uiState.showDocumentSelectionScreen = false;
            });
            try {
                if (this.onRecordsShared) {
                    this.updateConsentLocations();
                    await this.onRecordsShared();
                }
            }
            finally {
                runInAction(() => {
                    this._uiState.isTransitioningToHurLoading = false;
                });
            }
            return;
        }
        runInAction(() => {
            this._uiState.excludedLoginKeys = [];
            this._uiState.includedLoginKeys = [];
            this._uiState.hasSeenDocumentSelectionScreen = true;
            this._uiState.showDocumentSelectionScreen = false;
        });
    }
    async updateExcludedLocations() {
        if (!this.retrieval)
            return;
        const excludedKeys = this.locationList.filter((loc) => loc.isExcluded).map((loc) => loc.key);
        await this.documentRetrievalManager.updateExcludedLocations(this.retrieval.batchId, excludedKeys);
    }
    backToDocumentSelectionScreen() {
        this._uiState.showFastenImmediately = false;
        this._uiState.showDocumentSelectionScreen = true;
    }
    async confirmContinueWithoutSharing() {
        if (this._uiState.pendingSkipLocationKey && this.retrieval) {
            this._uiState.skippedLoginKeys.push(this._uiState.pendingSkipLocationKey);
            await this.documentRetrievalManager.skipLocation(this.retrieval.batchId, this._uiState.pendingSkipLocationKey);
            runInAction(() => {
                this._uiState.pendingSkipLocationKey = null;
                this._uiState.hasDeclinedSharing = false;
                this.moveToNextLoginLocation();
            });
        }
        else {
            if (this.isIAS) {
                runInAction(() => {
                    this._uiState.isTransitioningToHurLoading = true;
                    this._uiState.hasDeclinedSharing = false;
                });
                try {
                    if (this.onRecordsShared) {
                        this.updateConsentLocations();
                        await this.onRecordsShared();
                    }
                }
                finally {
                    runInAction(() => {
                        this._uiState.isTransitioningToHurLoading = false;
                    });
                }
                return;
            }
            if (this.onRecordsShared) {
                this.updateConsentLocations();
                await this.onRecordsShared();
            }
        }
    }
    continueToStartLoginCycle() {
        const loginRequiredLocationKeys = this.locationsNeedingAuth.map((loc) => loc.key);
        runInAction(() => {
            this._uiState.forceShowRecordsFoundListScreen = false;
            this._uiState.loginRequiredLocationKeys = loginRequiredLocationKeys;
            this._uiState.originalLoginLocationKeys = [...loginRequiredLocationKeys];
            this._uiState.currentLoginIndex = 0;
            this._uiState.isInLoginCycle = true;
            if (loginRequiredLocationKeys.length > 0) {
                this._uiState.currentLocationKeyForLogin = loginRequiredLocationKeys[0];
            }
        });
    }
    retryLocations(locations) {
        for (const location of locations) {
            this.onLocationIncluded(location);
        }
        const loginRequiredLocationKeys = locations.map((loc) => loc.key);
        runInAction(() => {
            this._uiState.loginRequiredLocationKeys = loginRequiredLocationKeys;
            this._uiState.originalLoginLocationKeys = [...loginRequiredLocationKeys];
            this._uiState.currentLoginIndex = 0;
            this._uiState.isInLoginCycle = true;
            if (loginRequiredLocationKeys.length > 0) {
                this._uiState.currentLocationKeyForLogin = loginRequiredLocationKeys[0];
            }
        });
    }
    onLoginSuccess() {
        this.moveToNextLoginLocation();
    }
    async onLoginSkipped() {
        if (!this._uiState.currentLocationKeyForLogin)
            return;
        const isWaitingForAuth = this._uiState.currentLocationKeyForLogin === this.authInProgress;
        if (isWaitingForAuth) {
            this._uiState.pendingSkipLocationKey = this._uiState.currentLocationKeyForLogin;
            this.facilitatedFHIRAuthManager.resetAuth();
            this.continueWithoutSharingRecords();
        }
        else {
            if (this.retrieval) {
                this._uiState.skippedLoginKeys.push(this._uiState.currentLocationKeyForLogin);
                await this.documentRetrievalManager.skipLocation(this.retrieval.batchId, this._uiState.currentLocationKeyForLogin);
            }
            this.moveToNextLoginLocation();
        }
    }
    onLocationExcluded(location) {
        this._uiState.excludedLoginKeys.push(location.key);
        this._uiState.includedLoginKeys = this._uiState.includedLoginKeys.filter((key) => key !== location.key);
    }
    onLocationIncluded(location) {
        this._uiState.includedLoginKeys.push(location.key);
        this._uiState.excludedLoginKeys = this._uiState.excludedLoginKeys.filter((key) => key !== location.key);
    }
    moveToNextLoginLocation() {
        runInAction(() => {
            this._uiState.currentLoginIndex++;
            const loginKeysLength = this._uiState.loginRequiredLocationKeys.length;
            if (this._uiState.currentLoginIndex < loginKeysLength) {
                this._uiState.currentLocationKeyForLogin = this._uiState.loginRequiredLocationKeys[this._uiState.currentLoginIndex];
            }
            else {
                this._uiState.isInLoginCycle = false;
                this._uiState.currentLocationKeyForLogin = null;
                const completedCount = this._uiState.completedLoginKeys.length;
                const skippedCount = this._uiState.skippedLoginKeys.length;
                const hasAnyFinishedLocations = this.locationsCompleted.length > 0 || this.locationsSkipped.length > 0;
                const allLoginCycleLocationsSkipped = completedCount === 0 && skippedCount === loginKeysLength;
                if (!hasAnyFinishedLocations && allLoginCycleLocationsSkipped) {
                    this._uiState.hasDeclinedSharing = true;
                }
            }
        });
    }
    get currentLocationForLogin() {
        if (!this._uiState.currentLocationKeyForLogin || !this.retrieval)
            return null;
        return this.retrieval.locations.find((loc) => loc.id === this._uiState.currentLocationKeyForLogin);
    }
    clearCurrentLocationForLogin() {
        this._uiState.currentLocationKeyForLogin = null;
        this._uiState.isInLoginCycle = false;
    }
    get hasCompletedLoginCycle() {
        if (this._uiState.hasCompletedStaticLoginCycle) {
            return true;
        }
        const loginKeysLength = this._uiState.loginRequiredLocationKeys.length;
        if (loginKeysLength > 0) {
            return !this._uiState.isInLoginCycle && this._uiState.currentLocationKeyForLogin === null;
        }
        else {
            return this.locationsNeedingAuth.length === 0;
        }
    }
    get hasMoreLocationsInLoginCycle() {
        var _a;
        const currentIndex = (_a = this._uiState.currentLoginIndex) !== null && _a !== void 0 ? _a : 0;
        const loginKeysLength = this._uiState.loginRequiredLocationKeys.length;
        return currentIndex + 1 < loginKeysLength;
    }
    get locationsNeedingAuth() {
        return this.locationList.filter((loc) => loc.status === DocumentRetrievalLocationStatus.NEEDS_AUTH && !loc.isExcluded);
    }
    get locationsSkipped() {
        return this.locationList.filter((loc) => loc.status === DocumentRetrievalLocationStatus.SKIPPED || loc.isExcluded);
    }
    get locationsCompleted() {
        const needsAuthKeys = new Set(this.locationsNeedingAuth.map((l) => l.key));
        const skippedKeys = new Set(this.locationsSkipped.map((l) => l.key));
        return this.locationList.filter((loc) => !needsAuthKeys.has(loc.key) && !skippedKeys.has(loc.key) && !loc.isExcluded);
    }
    get anyLocationNeedsAuth() {
        return this.locationList.some((loc) => loc.status === DocumentRetrievalLocationStatus.NEEDS_AUTH);
    }
    get anyLocationAddedViaFasten() {
        return this.locationList.some((loc) => loc.authType === DocumentRetrievalAuthType.FASTEN);
    }
    get anyLocationNotAddedViaFasten() {
        return this.locationList.some((loc) => loc.authType !== DocumentRetrievalAuthType.FASTEN);
    }
    get recordsFoundCount() {
        if (!this.retrieval)
            return 0;
        return this.retrieval.documents.length;
    }
    get locationsFoundCount() {
        if (!this.retrieval)
            return 0;
        return this.retrieval.locations.length;
    }
    get gettingLatestRecords() {
        return this.patientConsentUIManager.gettingLatestRecords;
    }
    get loadingProgress() {
        if (!this._uiState.now || !this._uiState.loadingStart)
            return 0.05;
        const expectedLoadingDuration = 30;
        const currentDuration = (this._uiState.now.getTime() - this._uiState.loadingStart.getTime()) / 1000;
        let progress = currentDuration / expectedLoadingDuration;
        progress = progress < 0 ? 0 : progress > 1 ? 1 : progress;
        progress = 0.05 + progress * 0.9;
        return progress;
    }
    get loadingTitle() {
        if (this.gettingLatestRecords) {
            return t("Finding your latest records...");
        }
        return t("Finding your records...");
    }
    get loadingSubTitle() {
        if (!this._uiState.now || !this._uiState.loadingStart) {
            return t("Connecting securely to your providers");
        }
        const elapsed = (this._uiState.now.getTime() - this._uiState.loadingStart.getTime()) / 1000;
        if (elapsed < 0) {
            return t("Connecting securely to your providers");
        }
        const cyclePosition = Math.floor(elapsed / 3) % 3;
        switch (cyclePosition) {
            case 0:
                return t("Connecting securely to your providers");
            case 1:
                return t("Retrieving your health records");
            case 2:
            default:
                return t("This may take up to a minute");
        }
    }
    get fastenPatientId() {
        var _a, _b;
        return (_b = (_a = this.patientInfo) === null || _a === void 0 ? void 0 : _a.patientId) !== null && _b !== void 0 ? _b : "";
    }
    get isLoggingInWithCLEAR() {
        return (this.smartAuthManager.state.state === SmartAuthState.IN_PROGRESS ||
            this.smartAuthManager.state.state === SmartAuthState.LOGGING_IN);
    }
    get clearLoginError() {
        if (!this.smartAuthManager.errorReason)
            return null;
        switch (this.smartAuthManager.errorReason) {
            case SmartAuthErrorReason.LOGIN_WINDOW_CLOSED:
                return t("The login window was closed, please try again.");
            case SmartAuthErrorReason.AUTH_FAILED:
                return t("The login failed, please try again.");
            case SmartAuthErrorReason.CLEAR_VALIDATION_FAILED:
                return t("We could not validate your identity, please try again");
            default:
                return t("An unexpected error occurred while logging in with CLEAR, please try again.");
        }
    }
    get animate() {
        return !this.patientConsentUIManager.isScreenshotMode;
    }
    startLoadingTimer() {
        this._uiState.loadingStart = new Date();
        this._uiState.now = new Date();
        this.loadingInterval = setInterval(() => {
            this.updateTime();
        }, 500);
    }
    clearLoadingTimer() {
        clearInterval(this.loadingInterval);
        this.loadingInterval = undefined;
    }
    updateTime() {
        this._uiState.now = new Date();
    }
    async providerSelectedWithPortal(provider, portal) {
        var _a, _b, _c;
        const brandId = (_a = provider.brandIds) === null || _a === void 0 ? void 0 : _a[0];
        const portalId = portal.id;
        const endpointId = (_c = (_b = portal.endpoints) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
        if (!brandId || !portalId || !endpointId) {
            console.error("Fasten provider missing required data:", { brandId, portalId, endpointId, provider });
            return;
        }
        await this.patientConsentUIManager.saveStateForAuthSession(this._uiState);
        await this.smartAuthManager.redirectToProviderPortalForFastenAuth(getFastenPublicId(this.authManager.isSandbox, this.environment), endpointId, brandId, portalId, this.fastenPatientId, null);
    }
    async suggestedFastenEndpointSelected(suggestion) {
        await this.patientConsentUIManager.saveStateForAuthSession(this._uiState);
        await this.smartAuthManager.redirectToProviderPortalForFastenAuth(getFastenPublicId(this.authManager.isSandbox, this.environment), suggestion.endpointId, suggestion.brandId, suggestion.portalId, this.fastenPatientId, null, DocumentRetrievalTaskSource.PARTICLE_FASTEN);
    }
    async epicLocationSelected(location) {
        var _a;
        try {
            const batchId = (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId;
            const redirectUrl = this.facilitatedFHIRAuthManager.getRedirectUri(EpicIntegration.OPEN_EPIC);
            if (!location.id || !location.url || !batchId) {
                console.error("Epic location missing required data or batch id", { id: location.id, url: location.url, batchId });
                return;
            }
            const urlAndState = await this.documentRetrievalManager.getFacilitatedFHIRAuthUrl(batchId, location.url, redirectUrl, DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC, EpicIntegration.OPEN_EPIC);
            if (!urlAndState) {
                console.warn(`Failed to get auth URL`);
                return;
            }
            await this.patientConsentUIManager.saveStateForAuthSession(this._uiState);
            await this.facilitatedFHIRAuthManager.redirectToFacilitatedFHIRLoginPage(location.id, urlAndState.url, urlAndState.state, window.location.hash, location.url);
        }
        catch (error) {
            console.error("Error handling epic location selected: ", error);
        }
    }
    async reauth() {
        void this.patientConsentUIManager.reAuth();
    }
    async retryTEFCARetrieval() {
        var _a;
        if (!((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId))
            return;
        await this.documentRetrievalManager.retryTEFCARetrieval(this.retrieval.batchId);
    }
    async skipLocation(locationKey) {
        if (!this.retrieval)
            return;
        await this.documentRetrievalManager.skipLocation(this.retrieval.batchId, locationKey);
    }
    async loginToLocation(locationKey) {
        await this.patientConsentUIManager.loginToLocationAndSaveState(locationKey, this._uiState);
    }
    restoreDocumentRetrievalUIState() {
        const documentRetrievalUIState = this.patientConsentUIManager.getDocumentRetrievalUIState();
        if (documentRetrievalUIState) {
            this._uiState = plainToInstance(DocumentRetrievalUIState, documentRetrievalUIState);
        }
    }
    async handleFastenCallbackForCustomUI(smartAuthData) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (smartAuthData.provider !== SmartAuthProvider.FASTEN) {
            console.warn("No smart auth data found for Fasten callback");
            return;
        }
        if (!smartAuthData.queryParams) {
            console.error("No query params found for Fasten callback");
            this.fastenAuthError = { locationKey: (_a = smartAuthData.locationKey) !== null && _a !== void 0 ? _a : null, error: SmartAuthErrorReason.SMART_AUTH_GENERIC };
            return;
        }
        const queryParams = new URLSearchParams((_b = smartAuthData.queryParams) !== null && _b !== void 0 ? _b : "");
        const isAuthorized = queryParams.get("connection_status") === "authorized";
        const commonData = {
            endpoint_id: (_c = queryParams.get("endpoint_id")) !== null && _c !== void 0 ? _c : "",
            brand_id: (_d = queryParams.get("brand_id")) !== null && _d !== void 0 ? _d : "",
            portal_id: (_e = queryParams.get("portal_id")) !== null && _e !== void 0 ? _e : "",
            external_id: (_f = queryParams.get("external_id")) !== null && _f !== void 0 ? _f : "",
            external_state: (_g = queryParams.get("external_state")) !== null && _g !== void 0 ? _g : "",
            connection_status: (_h = queryParams.get("connection_status")) !== null && _h !== void 0 ? _h : "",
            platform_type: (_j = queryParams.get("platform_type")) !== null && _j !== void 0 ? _j : "",
            request_id: (_k = queryParams.get("request_id")) !== null && _k !== void 0 ? _k : "",
        };
        const apiMode = getFastenApiMode(this.authManager.isSandbox);
        const fastenEvent = isAuthorized
            ? {
                api_mode: apiMode,
                event_type: FastenCallbackEventType.CONNECTION_SUCCESS,
                data: { ...commonData, org_connection_id: (_l = queryParams.get("org_connection_id")) !== null && _l !== void 0 ? _l : "" },
            }
            : {
                api_mode: apiMode,
                event_type: FastenCallbackEventType.CONNECTION_FAILED,
                data: {
                    ...commonData,
                    error: (_m = queryParams.get("error")) !== null && _m !== void 0 ? _m : "",
                    error_description: (_o = queryParams.get("error_description")) !== null && _o !== void 0 ? _o : "",
                    error_uri: (_p = queryParams.get("error_uri")) !== null && _p !== void 0 ? _p : "",
                },
            };
        try {
            if (fastenEvent.data.external_id !== this.fastenPatientId) {
                console.warn(`Unmatched patient id:${commonData.external_id} and ${this.fastenPatientId}`);
                return;
            }
            if (!this.retrieval) {
                console.warn(`No retrieval found`);
                return;
            }
            if (fastenEvent.event_type === FastenCallbackEventType.CONNECTION_SUCCESS) {
                this.fastenAuthError = null;
                await this.documentRetrievalManager.getFastenDocuments(this.retrieval.batchId, fastenEvent.data.org_connection_id, fastenEvent.data.portal_id, fastenEvent.data.brand_id, fastenEvent.data.endpoint_id, (_q = smartAuthData.source) !== null && _q !== void 0 ? _q : undefined);
                this.documentRetrievalManager.clearPollingTimerIfStartedByFastenCallback();
                this._uiState.hasDeclinedSharing = false;
            }
            else if (fastenEvent.event_type === FastenCallbackEventType.CONNECTION_FAILED) {
                this.fastenAuthError = { locationKey: (_r = smartAuthData.locationKey) !== null && _r !== void 0 ? _r : null, error: SmartAuthErrorReason.AUTH_FAILED };
            }
        }
        catch (error) {
            console.warn(`Fasten callback error: ${error}`);
        }
    }
    async handleEpicLocationLoginSuccess(key, code, fhirEndpoint) {
        try {
            if (!this.retrieval) {
                console.warn(`No retrieval found for Epic login success`);
                return;
            }
            await this.documentRetrievalManager.getFacilitatedFHIRRecordsForNewLocation(this.retrieval.batchId, key, fhirEndpoint, DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC, this.facilitatedFHIRAuthManager.getRedirectUri(EpicIntegration.OPEN_EPIC), code);
            this._uiState.hasDeclinedSharing = false;
        }
        catch (error) {
            console.warn(`Epic location login success error: ${error}`);
        }
    }
    get hurLoadingTitle() {
        return this.isIAS ? t("Building your HealthEx Unified Record...") : t("Sharing records and making a copy for you...");
    }
    get hurLoadingSubtitle() {
        var _a;
        if (this.isIAS) {
            const preferences = this.patientPreferencesManager.preferences.value;
            if (preferences === NOT_LOADED)
                return "";
            const preferredContactMethod = (_a = preferences === null || preferences === void 0 ? void 0 : preferences.preferredContactMethod) !== null && _a !== void 0 ? _a : ContactMethod.EMAIL;
            if (preferredContactMethod === ContactMethod.EMAIL) {
                return t("You can leave this page and once completed we'll email a copy of your records to {{ email }}.", {
                    email: this.authManager.email,
                });
            }
            else if (preferredContactMethod === ContactMethod.TEXT) {
                return t("You can leave this page and once completed we'll send a copy of your records to {{ phone }}.", {
                    phone: this.authManager.formattedPhone,
                });
            }
            else {
                return t("You can leave this page and check back later.");
            }
        }
        else {
            return t("We’re building your HealthEx Unified Record — your personal copy of everything we found.");
        }
    }
    get hurLoadingTimeAtMax() {
        var _a;
        const isFasten = (_a = this.patientConsentUIManager.retrieval) === null || _a === void 0 ? void 0 : _a.locations.some((location) => location.source === DocumentRetrievalTaskSource.FASTEN || location.source === DocumentRetrievalTaskSource.PARTICLE_FASTEN);
        const finalizeTime = isFasten ? 180 : 60;
        const vectorizationTime = this.isAIFlow ? 120 : 0;
        return finalizeTime + vectorizationTime;
    }
    hurLoadingGoToHealthEx() {
        this.patientConsentUIManager.close();
    }
}
DocumentRetrievalViewModel.pddaRoutes = new Set(PDDA_ALL_ROUTES);
__decorate([
    observable
], DocumentRetrievalViewModel.prototype, "_uiState", void 0);
__decorate([
    observable
], DocumentRetrievalViewModel.prototype, "fastenAuthError", void 0);
__decorate([
    observable
], DocumentRetrievalViewModel.prototype, "fastenCustomSearchModalOpen", void 0);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "retrievalError", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isAIFlow", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isVectorizationInProgress", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "fastenPublicId", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isVectorizationComplete", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "canAddLocations", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "zeroLocationsFound", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "explanationText", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "needsToReauthWithCLEAR", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "getAuthErrorText", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isAuthLoading", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "locationList", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "locationStatusCounts", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isInHurLoading", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "facilitatedFhirAuthInProgress", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "facilitatedFhirAuthErrorKey", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "facilitatedFhirAuthErrorMessage", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "fastenAuthInProgress", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "fastenAuthErrorKey", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "fastenAuthErrorMessage", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "authInProgress", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "authErrorKey", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "authErrorMessage", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "patientInfo", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isLoading", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isTEFCAError", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "useTefca", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "currentStep", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "showDocumentSelectionScreen", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isIAS", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "currentLocationForLogin", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "hasCompletedLoginCycle", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "hasMoreLocationsInLoginCycle", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "locationsNeedingAuth", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "locationsSkipped", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "locationsCompleted", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "anyLocationNeedsAuth", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "anyLocationAddedViaFasten", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "anyLocationNotAddedViaFasten", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "recordsFoundCount", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "locationsFoundCount", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "gettingLatestRecords", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "loadingProgress", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "loadingTitle", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "loadingSubTitle", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "fastenPatientId", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "isLoggingInWithCLEAR", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "clearLoginError", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "animate", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "hurLoadingTitle", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "hurLoadingSubtitle", null);
__decorate([
    computed
], DocumentRetrievalViewModel.prototype, "hurLoadingTimeAtMax", null);
